import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import React, { useCallback, useState } from 'react';

import Icon from '@/elements/Icon';
import Popover, { Props as PopoverProps } from '@/components/Popover';
import styled from '@/styles';
import { colors, paddings } from '@/styles/defaults';

// Sample Usage
// tooltipDetails = {
//   title: 'requested',
//   titleColor: colors.secondaryYellow700,
//   titleBackground: colors.secondaryYellow50,
//   text: 'Awaiting worker to start Checkr.',
//   subText: 'Requested Feb 28, 2025',
// }
// <TooltipInfoV2
//   title={tooltipDetails.title}
//   titleColor={tooltipDetails.titleColor}
//   titleBackground={tooltipDetails.titleBackground}
//   text={tooltipDetails.text}
//   subText={tooltipDetails.subText}
// />

const Wrapper = styled('div', {
  padding: paddings.padding3x,
  backgroundColor: '#FFF',
  borderRadius: paddings.padding2x,
  boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.12)',
  lineHeight: 0,
  width: '244px',
  maxWidth: '244px',
});

const InvertedTriangle = styled('div', {
  width: 0,
  height: 0,
  borderLeft: '10px solid transparent',
  borderRight: '10px solid transparent',
  borderTop: '10px solid #FFF',
  right: '10px',
  bottom: '-8px',
  boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.12)',
  position: 'absolute',
});

const TooltipInfoV2 = ({
  title,
  titleColor,
  titleBackground,
  text,
  subText,
}: {
  title: string;
  titleColor: string;
  titleBackground: string;
  text: string;
  subText: string;
}) => {
  const TitleContainer = styled('div', {
    marginBottom: '8px',
    borderRadius: paddings.padding2x,
    padding: '0px 8px',
    height: '24px',
    backgroundColor: titleBackground,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '100px',
  });

  const TitleText = styled('span', {
    color: titleColor,
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '16.8px',
    fontStyle: 'normal',
    textTransform: 'uppercase',
  });

  const MainText = styled('span', {
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '19.6px',
    color: colors.neutralDarkGray900,
  });

  const SubText = styled('span', {
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '16.8px',
    color: colors.neutralDarkGray700,
  });

  const [isActive, setIsActive] = useState(false);
  const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);

  const setActive = useCallback(() => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
    console.log('setActive');
    setIsActive(true);
  }, [hideTimeout]);

  const setInactive = useCallback(() => {
    const timeout = setTimeout(() => {
      setIsActive(false);
    }, 200); // 200ms delay before hiding
    setHideTimeout(timeout);
  }, []);

  const handleKeyCommands = useCallback(
    (ev: React.KeyboardEvent<HTMLDivElement>) => {
      // https://www.w3.org/TR/wai-aria-practices-1.1/#keyboard-interaction-21
      if (ev.key === 'Escape') {
        ev.stopPropagation();
        setIsActive(false);
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          setHideTimeout(null);
        }
      }
    },
    [hideTimeout],
  );

  const tooltipContent = (
    <Wrapper>
      <InvertedTriangle />
      {title && (
        <TitleContainer>
          <TitleText>{title}</TitleText>
        </TitleContainer>
      )}
      <div style={{ display: 'block' }}>
        <MainText>{text}</MainText>
      </div>
      <div style={{ display: 'block' }}>
        <SubText>{subText}</SubText>
      </div>
    </Wrapper>
  );

  return (
    <div
      style={{
        position: 'absolute',
        right: 0,
      }}
      onKeyDown={handleKeyCommands}
      onBlur={() => {
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          setHideTimeout(null);
        }
        setIsActive(false);
      }}
      onFocus={setActive}
      onMouseEnter={setActive}
      onMouseLeave={setInactive}
    >
      <Icon icon={faInfoCircle} theme="lighter" />
      <Popover
        disableClickout
        align={'aboveHigher'}
        id={'tooltip-info-v2'}
        isOpen={isActive}
        justify={'fromRight'}
        role="tooltip"
        onRequestClose={() => undefined}
      >
        <div onMouseEnter={setActive} onMouseLeave={setInactive}>
          {tooltipContent}
        </div>
      </Popover>
    </div>
  );
};

export default TooltipInfoV2;
